import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash'
import format from 'date-fns/format'
import setYear from 'date-fns/set_year'
import addMonths from 'date-fns/add_months';
import lastDayOfMonth from 'date-fns/last_day_of_month';
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days'
import differenceInCalendarMonths from 'date-fns/difference_in_calendar_months'
import differenceInCalendarYears from 'date-fns/difference_in_calendar_years'
import {globalfunc} from '../globalfunc.js'
import { getCompany, getTeamByCompany, invalidateMemberAndTeamCaches } from './core/cacheService'

// Thresholds for structural-issue tagging on the Managers tab.
const LARGE_SPAN_THRESHOLD = 12
const DEEP_CHAIN_THRESHOLD = 4

function bossWalk(manager: any, memberKey: any) {
  const lineage: any[] = []
  const seen = new Set<string>()
  let cursor: any = manager
  let depth = 0
  while (cursor && cursor.boss) {
    const k_boss = String(cursor.boss).replace(/\./g, '||')
    if (seen.has(k_boss)) break
    seen.add(k_boss)
    const next = memberKey[k_boss]
    if (!next) break
    depth++
    lineage.unshift({
      firstname: next.firstname || '',
      lastname: next.lastname || '',
      email: next.email || ''
    })
    cursor = next
  }
  return { depth, lineage }
}

// Returns total descendants (inclusive of directs). Memoized across calls
// in the same `getMemberManager` pass so each subtree is walked once even
// when many ancestors share it.
function makeDescendantCounter(userSub: any) {
  const memo = new Map<string, number>()
  function count(email: string, stack: Set<string>): number {
    if (!email || stack.has(email)) return 0
    if (memo.has(email)) return memo.get(email)!
    stack.add(email)
    const direct = userSub[email] || []
    let total = direct.length
    for (const child of direct) {
      if (child && child.email && child.email !== email) {
        total += count(child.email, stack)
      }
    }
    stack.delete(email)
    memo.set(email, total)
    return total
  }
  return (email: string) => count(email, new Set<string>())
}

function buildSubTeamCounts(allTeams: any): { [k: string]: number } {
  const counts: { [k: string]: number } = {}
  for (const k in allTeams) {
    const t = allTeams[k]
    if (t && t.parent) {
      counts[t.parent] = (counts[t.parent] || 0) + 1
    }
  }
  return counts
}

function applyManagerFilter(items: any[], filter: string): any[] {
  if (!filter || filter === 'all') return items
  if (filter === 'inactive') return items.filter((m: any) => m.status === 'inactive')
  if (filter === 'invited') return items.filter((m: any) => m.status === 'invited')
  if (filter === 'active') return items.filter((m: any) => m.status === 'active')
  if (filter === 'issues') return items.filter((m: any) => m.issues && m.issues.length > 0)
  // issue-tag filters: 'no-reports' | 'large-span' | 'deep-chain'
  return items.filter((m: any) => m.issues && m.issues.indexOf(filter) !== -1)
}

function applyDepthFilter(items: any[], depthFilter: number[]): any[] {
  if (!depthFilter || depthFilter.length === 0) return items
  const allowed = new Set(depthFilter)
  return items.filter((m: any) => allowed.has(m.managementDepth || 0))
}

function applyManagerSort(items: any[], sortBy: string): any[] {
  if (sortBy === 'direct') {
    return _.orderBy(items, [(o: any) => o.sub_total || 0], ['desc'])
  }
  if (sortBy === 'span') {
    // totalReports already includes directs — don't double-count.
    return _.orderBy(items, [(o: any) => o.totalReports || 0], ['desc'])
  }
  if (sortBy === 'depth') {
    return _.sortBy(items, [(o: any) => o.managementDepth || 0])
  }
  if (sortBy === 'active') {
    // Oldest-active first surfaces dormant managers; missing `updated` sorts last.
    return _.sortBy(items, [(o: any) => o.updated || '9999'])
  }
  return _.sortBy(items, [(o: any) => o.firstname])
}

export default {
    namespaced: true,
    state: {
      managers:[],
      managersall:[],
      teamall:[],
      inputSearch: '',
      page:1,
      // btNext:false,
      // btprevious:false,
      size_page:10,
      sortfield:'value',
      sortorder:'asc',
      results: 0,
      sortBy: 'firstname',
      activeFilter: 'all',
      depthFilter: [] as number[],
      availableDepths: [] as number[],
      kpiCounts: { total: 0, inactive: 0, orphaned: 0, largeSpan: 0, deepChain: 0 },
      subManagerByKey: [],
      membersByKey: [],
      bossByKey: [],
      teamByKey: [],
      loadingManagersTable: true,
    },
    mutations: {
      setManager(state:any, result:any) {
        state.managers = result
      },
      setManagerall(state:any, result:any) {
        state.managersall = result
      },
      setSubManager(state:any, result:any) {
        state.subManagerByKey = result
      },
      setTeamall(state:any, result:any) {
        state.teamall = result
      },
      setinputSearch(state:any,value:any) {
        state.inputSearch = value.keyword
      },
      setPage(state:any,result:any) {
        state.page = result
      },
      // setbtNext(state:any,result:any) {
      //   state.btNext = result
      // },
      // setbtPrevious(state:any,result:any) {
      //   state.btprevious = result
      // },
      setSortfield(state:any,result:any) {
        state.sortfield = result
      },
      setSortorder(state:any,result:any) {
        state.sortorder = result
      },
      setResults(state:any, result:any) {
        state.results = result
      },
      setMemberKey(state:any, result:any) {
        state.membersByKey = result
      },
      setBossKey(state:any, result:any) {
        state.bossByKey = result
      },
      setTeamKey(state:any, result:any) {
        state.teamByKey = result
      },
      setLoadingManagersTable(state:any, result:any) {
        state.loadingManagersTable = result
      },
      setActiveFilter(state:any, value:string) {
        state.activeFilter = value || 'all'
      },
      setKpiCounts(state:any, value:any) {
        state.kpiCounts = value || { total: 0, inactive: 0, orphaned: 0, largeSpan: 0, deepChain: 0 }
      },
      setDepthFilter(state:any, value:number[]) {
        state.depthFilter = Array.isArray(value) ? value.slice() : []
      },
      setAvailableDepths(state:any, value:number[]) {
        state.availableDepths = Array.isArray(value) ? value.slice() : []
      },
    },
    actions: {
      async getMemberManager({commit,dispatch,state,rootState}:any, user:any){
        commit('setLoadingManagersTable',true)
        commit('setManager',[])
        let dateNow = new Date()
        let count = 0
        let thisYear = parseInt(format(dateNow, 'YYYY'))
        const users:Array<any> = [];
        const memberKey:Array<any> = [];
        const userSub:Array<any> = [];
        const allBoss:Array<any> = [];
        const team =await getTeamByCompany(user.company)// (await firebase.database().ref('team').orderByChild("company").equalTo(user.company).once('value')).val()
        let allUser={}
        if(rootState.newmember.updateMember==true){
          allUser =  await dispatch('main/getuser',true,{root:true})
        }
        else if(Object.keys(rootState.main.allUser).length>0){
     
           allUser = rootState.main.allUser
        }else{
          allUser =  await dispatch('main/getuser',true,{root:true})
        }
        //let allUser = rootState.main.allUser//(await firebase.database().ref('users').orderByChild("company").equalTo(user.company).once('value')).val()
        const company = await getCompany(user.company)
        const usersManager:Array<any> = [];
        for(const arrData in allUser){

          const u = allUser[arrData]
          u.key = arrData
        
          u.value = allUser[arrData].firstname + " " + allUser[arrData].lastname
          
          let k_boss = allUser[arrData].boss.replace(/\./g, "||")
          if(k_boss && allUser[k_boss] != undefined){
            u.boss_name = allUser[k_boss]["firstname"] + " " + allUser[k_boss]["lastname"]
            u.boss_firstname = allUser[k_boss]["firstname"]
            u.boss_lastname = allUser[k_boss]["lastname"]
            u.boss_shortname = allUser[k_boss]["firstname"] + " " + allUser[k_boss]["lastname"].charAt(0)
            if(allUser[k_boss]['profilepic_small']){
              u.boss_pic = allUser[k_boss]['profilepic_small'];
            }else if (allUser[k_boss]['profilepic_crop']){
              u.boss_pic = allUser[k_boss]['profilepic_crop'];
            }else if (allUser[k_boss]['profilepic']){
              u.boss_pic = allUser[k_boss]['profilepic'];
            }else {
              u.boss_pic = "";
            }
          }
          if(u.team) u.team_name = (team[u.team]!=undefined)?team[u.team]["name"]:''
          if(u.team) u.team_color = (team[u.team]!=undefined)?team[u.team]["color"]:''
          if(u.profilepic_small){
            u.pic = u.profilepic_small;
          }else if (u.profilepic_crop){
            u.pic = u.profilepic_crop;
          }else if (u.profilepic){
            u.pic = u.profilepic;
          }else {
            u.pic = "";
          }

          if(u.profilepic) {
            u.large_pic = u.profilepic
          }else if(u.profilepic_crop) {
            u.large_pic = u.profilepic_crop
          }else if(u.profilepic_small){
            u.large_pic = u.profilepic_small
          }else {
            u.large_pic = ""
          }

          // if(company.medal && u.medal){
          //   _.forEach(u.medal, (value,key)=> {
          //     u.medal[key] = company.medal[key]
          //   });
          // }
          if(u.tutorial && u.tutorial == 'Y'){
            let dayActive = differenceInCalendarDays(dateNow, u.updated)
            if(dayActive > 30){
              u.status = 'inactive'
              u.to_now = format(u.updated,'MMM D, YYYY')
            }else{
              u.status = 'active'
              u.to_now = globalfunc.timeSince(u.updated)
            }
          }else if(u.invited){
            u.status = 'invited'
            u.to_now = globalfunc.timeSince(u.updated)
          }else{
            u.status = 'new'
          }
          if(u.joined_tooltips === undefined){
            u.joined_tooltips = new Array()
          }
          if(u.workdate){
            u.joined = format(u.workdate,'MMMM D, YYYY')
            let arrJoined:Array<any> = [];
            arrJoined['year'] = differenceInCalendarYears(dateNow, u.workdate)
            let month = differenceInCalendarMonths(dateNow, setYear(u.workdate,thisYear))
            if(month < 0){
              arrJoined['month'] = month + 12
              arrJoined['year'] = (arrJoined['year'] > 0) ? arrJoined['year'] - 1 : 0
            }else{
              arrJoined['month'] = month
            }
            let day = parseInt(format(dateNow,'DD')) - parseInt(format(u.workdate,'DD'))
            if(day < 0){
              arrJoined['day'] = day + parseInt(format(lastDayOfMonth(addMonths(dateNow, -1)),'DD'));
              if(arrJoined['month'] > 0){
                arrJoined['month'] = arrJoined['month'] - 1
              }else{
                arrJoined['month'] = 12
                arrJoined['year'] = (arrJoined['year'] > 0) ? arrJoined['year'] - 1 : 0
              }
            }else{
              arrJoined['day'] = day
            }
            u.joined_tooltips.push(arrJoined)
          }
          if(u.boss){
            allBoss[k_boss] = u.boss
            if(userSub[u.boss] === undefined){
              userSub[u.boss] = new Array()
            }
            userSub[u.boss].push(u)
          }
          memberKey[u.key] = u
          users.push(u)

        }
        Object.entries(allBoss).forEach(([key, item]) =>{
          let _item = memberKey[key]
          if(_item){
            _item.sub_total = (userSub[_item.email])?userSub[_item.email].length:0
            usersManager.push(_item)
          }
        });
        let _usersCheck:Array<any> = [];
        _usersCheck = _.sortBy(users, [function(o:any) { return o.workdate; }])
        let orderJoin = 1
        _.forEach(_usersCheck, (item,key)=> {
          if(item.workdate){
            _usersCheck[key].order_join = orderJoin
            orderJoin++
          }
        });
        _.forEach(usersManager, (item,key)=> {
          let managerExist = _.filter(_usersCheck, {
            email: item.email
          });
          if(item.workdate && managerExist.length > 0){
            usersManager[key].order_join = managerExist[0].order_join
          }
        });

        const subTeamCounts = buildSubTeamCounts(team)
        const countDescendants = makeDescendantCounter(userSub)
        const kpi = { total: usersManager.length, inactive: 0, orphaned: 0, largeSpan: 0, deepChain: 0 }
        _.forEach(usersManager, (m: any) => {
          const directCount = m.sub_total || 0
          const totalReports = countDescendants(m.email)
          const walk = bossWalk(m, memberKey)
          m.totalReports = totalReports
          m.managementDepth = walk.depth
          m.lineage = walk.lineage
          m.subTeamCount = m.team ? (subTeamCounts[m.team] || 0) : 0

          const issues: string[] = []
          if (m.status === 'inactive' && totalReports > 0) issues.push('inactive')
          if (m.status === 'invited' && totalReports > 0) issues.push('invited')
          if (directCount === 0 && m.status !== 'invited') issues.push('no-reports')
          if (directCount >= LARGE_SPAN_THRESHOLD) issues.push('large-span')
          if (walk.depth >= DEEP_CHAIN_THRESHOLD) issues.push('deep-chain')
          m.issues = issues

          if (issues.indexOf('inactive') !== -1) kpi.inactive++
          if (issues.indexOf('no-reports') !== -1) kpi.orphaned++
          if (issues.indexOf('large-span') !== -1) kpi.largeSpan++
          if (issues.indexOf('deep-chain') !== -1) kpi.deepChain++
        })

        const depthSet = new Set<number>()
        usersManager.forEach((m: any) => depthSet.add(m.managementDepth || 0))
        const availableDepths = Array.from(depthSet).sort((a, b) => a - b)
        commit('setAvailableDepths', availableDepths)

        var _users = _.sortBy(usersManager, [function(o:any) { return o.firstname; }])
        commit('setBossKey', allBoss)
        commit('setTeamKey', team)
        commit('setMemberKey', memberKey)
        commit('setSubManager',userSub)
        commit('setKpiCounts', kpi)
        let filteredAll = applyManagerFilter(usersManager, state.activeFilter)
        filteredAll = applyDepthFilter(filteredAll, state.depthFilter)
        commit('setResults', filteredAll.length)
        commit('setManager', _.sortBy(filteredAll.slice(0, state.size_page), [function(o:any) { return o.firstname; }]))
        commit('setManagerall', _.sortBy(usersManager, [function(o:any) { return o.firstname; }]))
        commit('setLoadingManagersTable',false)
        if(state.inputSearch){
          dispatch('searchMembers',state.inputSearch)
        }
        // await firebase.database().ref().child('users').orderByChild('company').equalTo(`${user.company}`).once('value',async snapshot => {
        //   const usersManager:Array<any> = [];
        //   snapshot.forEach((arrData):any => {
        //     const u = arrData.val()
        //       u.key = arrData.key
            
        //       u.value = arrData.val().firstname + " " + arrData.val().lastname
              
        //       let k_boss = arrData.val().boss.replace(/\./g, "||")
        //       if(k_boss && allUser[k_boss] != undefined){
        //         u.boss_name = allUser[k_boss]["firstname"] + " " + allUser[k_boss]["lastname"]
        //         u.boss_firstname = allUser[k_boss]["firstname"]
        //         u.boss_lastname = allUser[k_boss]["lastname"]
        //         u.boss_shortname = allUser[k_boss]["firstname"] + " " + allUser[k_boss]["lastname"].charAt(0)
        //         if(allUser[k_boss]['profilepic_small']){
        //           u.boss_pic = allUser[k_boss]['profilepic_small'];
        //         }else if (allUser[k_boss]['profilepic_crop']){
        //           u.boss_pic = allUser[k_boss]['profilepic_crop'];
        //         }else if (allUser[k_boss]['profilepic']){
        //           u.boss_pic = allUser[k_boss]['profilepic'];
        //         }else {
        //           u.boss_pic = "";
        //         }
        //       }
        //       if(u.team) u.team_name = (team[u.team]!=undefined)?team[u.team]["name"]:''
        //       if(u.team) u.team_color = (team[u.team]!=undefined)?team[u.team]["color"]:''
        //       if(u.profilepic_small){
        //         u.pic = u.profilepic_small;
        //       }else if (u.profilepic_crop){
        //         u.pic = u.profilepic_crop;
        //       }else if (u.profilepic){
        //         u.pic = u.profilepic;
        //       }else {
        //         u.pic = "";
        //       }

        //       if(u.profilepic) {
        //         u.large_pic = u.profilepic
        //       }else if(u.profilepic_crop) {
        //         u.large_pic = u.profilepic_crop
        //       }else if(u.profilepic_small){
        //         u.large_pic = u.profilepic_small
        //       }else {
        //         u.large_pic = ""
        //       }

        //       if(company.medal && u.medal){
        //         _.forEach(u.medal, (value,key)=> {
        //           u.medal[key] = company.medal[key]
        //         });
        //       }
        //       if(u.tutorial && u.tutorial == 'Y'){
        //         let dayActive = differenceInCalendarDays(dateNow, u.updated)
        //         if(dayActive > 30){
        //           u.status = 'inactive'
        //           u.to_now = format(u.updated,'MMM D, YYYY')
        //         }else{
        //           u.status = 'active'
        //           u.to_now = globalfunc.timeSince(u.updated)
        //         }
        //       }else if(u.invited){
        //         u.status = 'invited'
        //         u.to_now = globalfunc.timeSince(u.updated)
        //       }else{
        //         u.status = 'new'
        //       }
        //       if(u.joined_tooltips === undefined){
        //         u.joined_tooltips = new Array()
        //       }
        //       if(u.workdate){
        //         u.joined = format(u.workdate,'MMMM D, YYYY')
        //         let arrJoined:Array<any> = [];
        //         arrJoined['year'] = differenceInCalendarYears(dateNow, u.workdate)
        //         let month = differenceInCalendarMonths(dateNow, setYear(u.workdate,thisYear))
        //         if(month < 0){
        //           arrJoined['month'] = month + 12
        //           arrJoined['year'] = (arrJoined['year'] > 0) ? arrJoined['year'] - 1 : 0
        //         }else{
        //           arrJoined['month'] = month
        //         }
        //         let day = parseInt(format(dateNow,'DD')) - parseInt(format(u.workdate,'DD'))
        //         if(day < 0){
        //           arrJoined['day'] = day + parseInt(format(lastDayOfMonth(addMonths(dateNow, -1)),'DD'));
        //           if(arrJoined['month'] > 0){
        //             arrJoined['month'] = arrJoined['month'] - 1
        //           }else{
        //             arrJoined['month'] = 12
        //             arrJoined['year'] = (arrJoined['year'] > 0) ? arrJoined['year'] - 1 : 0
        //           }
        //         }else{
        //           arrJoined['day'] = day
        //         }
        //         u.joined_tooltips.push(arrJoined)
        //       }
        //       if(u.boss){
        //         allBoss[k_boss] = u.boss
        //         if(userSub[u.boss] === undefined){
        //           userSub[u.boss] = new Array()
        //         }
        //         userSub[u.boss].push(u)
        //       }
        //       memberKey[u.key] = u
        //       users.push(u)
        //   });
        //   Object.entries(allBoss).forEach(([key, item]) =>{
        //     let _item = memberKey[key]
        //     if(_item){
        //       _item.sub_total = (userSub[_item.email])?userSub[_item.email].length:0
        //       usersManager.push(_item)
        //     }
        //   });
        //   let _usersCheck:Array<any> = [];
        //   _usersCheck = _.sortBy(users, [function(o:any) { return o.workdate; }])
        //   let orderJoin = 1
        //   _.forEach(_usersCheck, (item,key)=> {
        //     if(item.workdate){
        //       _usersCheck[key].order_join = orderJoin
        //       orderJoin++
        //     }
        //   });
        //   _.forEach(usersManager, (item,key)=> {
        //     let managerExist = _.filter(_usersCheck, {
        //       email: item.email
        //     });
        //     if(item.workdate && managerExist.length > 0){
        //       usersManager[key].order_join = managerExist[0].order_join
        //     }
        //   });
        //   var _users = _.sortBy(usersManager, [function(o:any) { return o.firstname; }])
        //   commit('setBossKey', allBoss)
        //   commit('setTeamKey', team)
        //   commit('setMemberKey', memberKey)
        //   commit('setSubManager',userSub)
        //   commit('setResults',usersManager.length)
        //   commit('setManager', _.sortBy(_users.slice(0, state.size_page), [function(o:any) { return o.firstname; }]))
        //   commit('setManagerall', _.sortBy(usersManager, [function(o:any) { return o.firstname; }]))
        //   commit('setLoadingManagersTable',false)
        //   if(state.inputSearch){
        //     dispatch('searchMembers',state.inputSearch)
        //   }
        // }).catch(error => {
        //   console.log(error)
        // });
      },
      async getTeamAll({commit,dispatch,state}:any, user:any){
        try{
          const team = await getTeamByCompany(user.company)
          const teamData:Array<any> = [];
          for(const key in team){
            const u = team[key]
            u.key = key
            u.value = team[key].name
            teamData.push(u)
          }
          commit('setTeamall', _.sortBy(teamData, [function(o:any) { return o.name; }]))
        }catch(error){
          console.log(error)
        }
       
        // firebase.database().ref('team').orderByChild("company").equalTo(`${user.company}`).once('value', snapshot => {
        //   const teamData:Array<any> = [];
        //   snapshot.forEach((arrData):any => {
        //     const u = arrData.val()
        //     u.key = arrData.key
        //     u.value = arrData.val().name
        //     teamData.push(u)
            
        //   });
        //   commit('setTeamall', _.sortBy(teamData, [function(o:any) { return o.name; }]))
        // }).catch(error => {
        //   console.log(error)
        // });
      },
      currentChange({commit,state}:any,page:any){
        var size = state.size_page
        var start_row = (page-1) * size
        var end_row = ((page-1) * size)+size
        let member: Array<any> = state.managersall
        if (state.inputSearch.length > 2){
          var re = new RegExp(state.inputSearch, 'gi')
          member = _.filter(member, (item): any => {
            if (item.firstname.match(re)) return true
            if (item.lastname != undefined && item.lastname.match(re)) return true
            if (item.value != undefined && item.value.match(re)) return true
            if (item.email != undefined && item.email.match(re)) return true
            if (item.team_name != undefined && item.team_name.match(re)) return true
          })
        }
        member = applyManagerFilter(member, state.activeFilter)
        member = applyDepthFilter(member, state.depthFilter)
        member = applyManagerSort(member, state.sortBy)
        commit('setPage', page)
        commit('setResults', member.length)
        commit('setManager', member.slice(start_row, end_row))
      },
      searchMembers({commit,state}:any,value:string){
        commit('setPage',1)
        let member: Array<any> = state.managersall
        if (value && value.length > 2){
          const re = new RegExp(value, 'gi')
          member = _.filter(member, (item): any => {
            if (item.firstname.match(re)) { item.priority = 1; return true }
            if (item.lastname != undefined && item.lastname.match(re)) { item.priority = 2; return true }
            if (item.value != undefined && item.value.match(re)) { item.priority = 3; return true }
            if (item.email != undefined && item.email.match(re)) { item.priority = 3; return true }
            if (item.team_name != undefined && item.team_name.match(re)) { item.priority = 3; return true }
          })
        }
        member = applyManagerFilter(member, state.activeFilter)
        member = applyDepthFilter(member, state.depthFilter)
        member = applyManagerSort(member, state.sortBy)
        commit('setResults', member.length)
        commit('setManager', member.slice(0, state.size_page))
      },
      sortBy({ commit, state, dispatch }: any, type: string) {
        state.sortBy = type
        commit('setPage', 1)
        dispatch('currentChange', 1)
      },
      setActiveFilter({ commit, dispatch }: any, value: string) {
        commit('setActiveFilter', value || 'all')
        commit('setPage', 1)
        dispatch('currentChange', 1)
      },
      setDepthFilter({ commit, dispatch }: any, value: number[]) {
        commit('setDepthFilter', Array.isArray(value) ? value : [])
        commit('setPage', 1)
        dispatch('currentChange', 1)
      },
      async assignManagerSubmit({commit,rootState,dispatch,state}:any, objData:any){
        let member = objData.memberData
        let manager = objData.manager
        const userComp = rootState.authen.user.company
        if(member && manager){
          const updatePromises = _.map(member, (item,key)=> {
            return firebase.database().ref(`/users/${item.key}`).update({
              boss:manager,
              modifydatetime:format(new Date(), 'YYYY-MM-DD HH:mm')
            }).then(()=>{
              /*save logs*/
              dispatch('authen/addLogs', {
                company: userComp,
                createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                id: item.key,
                table: 'users',
                status: 'assignManager',
                user: rootState.authen.user.email
              }, {root:true})
            });
          });
          await Promise.all(updatePromises)
          invalidateMemberAndTeamCaches(userComp)
          await dispatch('main/refreshTeamsAndHeight', null, { root: true })
          await firebase.database().ref(`/genboss`).push({
            company:userComp
          });
          await firebase.database().ref(`/genorganize`).push({
            company:userComp
          })
          commit('newmember/setUpdateMember', true, {root:true})
          commit('setPage',1)
          await dispatch('getMemberManager',rootState.authen.user)
        }
      },
    },
    getters: {
        managers : (state:any) => {
            return state.managers
        },
        managersall : (state:any) => {
          return state.managersall
        },
        subManagerByKey : (state:any) => {
          return state.subManagerByKey
        },
        team : (state:any) => {
          return state.teamall
        },
        
        inputSearch: (state:any) => {
          return state.inputSearch
        },
        // btNext: (state:any) => {
        //   return state.btNext
        // },
        // btprevious: (state:any) => {
        //   return state.btprevious
        // },
        sizepage: (state:any) => {
          return state.size_page
        },
        results : (state:any) => {
          return state.results
        },
        page : (state:any) => {
          return state.page
        },
        membersByKey : (state:any) => {
          return state.membersByKey
        },
        bossByKey : (state:any) => {
          return state.bossByKey
        },
        teamByKey : (state:any) => {
          return state.teamByKey
        },
        loadingManagersTable : (state:any) => {
          return state.loadingManagersTable
        },
        activeFilter : (state:any) => {
          return state.activeFilter
        },
        kpiCounts : (state:any) => {
          return state.kpiCounts
        },
        currentSortBy : (state:any) => {
          return state.sortBy
        },
        depthFilter : (state:any) => {
          return state.depthFilter
        },
        availableDepths : (state:any) => {
          return state.availableDepths
        },
    }
  }
